import { NamedModel } from 'api/Exec/types';
import ModelExecutionResults from 'app/common_components/ModelExecutionResults/ModelExecutionResults';
import { formatDateTime } from 'app/helpers/dateTime';
import { FormValues } from 'components/Dashboard/Models/NamedModels/NamedModelForm/NamedModelForm';

import React, { CSSProperties } from 'react';
import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import { Descriptions, Form } from 'antd';
import type { DescriptionsProps } from 'antd';

interface Props {
  model: NamedModel | null;
}

const DetailedNamedModel: React.FC<Props> = ({ model }) => {
  const [ form ] = Form.useForm<FormValues>();

  if (!model) {
    return null;
  }

  const positiveStyles: CSSProperties = { color: '#00B96B', marginRight: 8 };
  const negativeStyles: CSSProperties = { color: '#D9D9D9', marginRight: 8 };

  const items: DescriptionsProps['items'] = [
    {
      label: 'Model name',
      children: model.modelName,
    },
    {
      label: 'Model API',
      children: model.modelApiName,
    },
    {
      label: 'Image tag',
      children: model.modelImageTag,
    },
    {
      label: (
        <>
          {model.isSandbox ? <CheckCircleFilled style={positiveStyles} /> : <CloseCircleFilled style={negativeStyles} />}
          Sandbox
        </>
      ),
      children: 'Model is a sandbox with random data',
    },
    {
      label: (
        <>
          {model.clinicalValidated ? <CheckCircleFilled style={positiveStyles} /> : <CloseCircleFilled style={negativeStyles} />}
          Clinical validated
        </>
      ),
      children: 'Model passed process of Clinical validation',
    },
    {
      label: (
        <>
          {model.enabled ? <CheckCircleFilled style={positiveStyles} /> : <CloseCircleFilled style={negativeStyles} />}
          Enabled
        </>
      ),
      children: 'Model is enabled and available',
    },
    {
      label: 'Validated at',
      children: model.validatedAt && formatDateTime(model.validatedAt),
    },
    {
      label: 'Validated by',
      children: model.validatedBy,
    },
    {
      label: 'Comment',
      children: model.comment,
    },
  ];

  return (
    <>
      <Descriptions bordered title={`ID: ${model.id}`} items={items} column={1} />
      <Form
        form={form}
        name="detailedNamedModel"
        layout="vertical"
        initialValues={{
          executionResults: {
            resultSubPop: model ? !!model.executionResults?.resultSubPop : false,
            resultSubPopChrm: model ? !!model.executionResults?.resultSubPopChrm : false,
            resultSuperPop: model ? !!model.executionResults?.resultSuperPop : false,
            resultSuperPopChrm: model ? !!model.executionResults?.resultSuperPopChrm : false,
            resultSvgData: model ? !!model.executionResults?.resultSvgData : false,
            resultPrsData: model ? !!model.executionResults?.resultPrsData : false,
            resultPrsTechData: model ? !!model.executionResults?.resultPrsTechData : false,
            resultPrsQc: model ? !!model.executionResults?.resultPrsQc : false,
          },
          postExecutionResults: {
            ancestryPdfReport: model ? !!model.postExecutionResults?.ancestryPdfReport : false,
            prsRuoCardioPdfReport: model ? !!model.postExecutionResults?.prsRuoCardioPdfReport : false,
            prsRuoCancerPdfReport: model ? !!model.postExecutionResults?.prsRuoCancerPdfReport : false,
            prsClinicalCardioPdfReport: model ? !!model.postExecutionResults?.prsClinicalCardioPdfReport : false,
            prsClinicalCancerPdfReport: model ? !!model.postExecutionResults?.prsClinicalCancerPdfReport : false,
          },
        }}
      >
        <ModelExecutionResults executionResults={model.executionResults} postExecutionResults={model.postExecutionResults} viewMode />
      </Form>
    </>
  );
};

export default DetailedNamedModel;
